import DropdownMenu from 'components/DropdownMenu';
import KoalaIconButton from 'koala/components/IconButton';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryCache } from 'react-query';
import styled from 'styled-components';
import { Webhook } from 'types';
import * as remoteApi from 'api/remote';
import queryKeys from 'config/queryKeys';
import KoalaLoader from 'koala/components/Loader';
import WebhookActiveButton from './WebhookActiveButton';
import theme from 'theme';

const WebhookRowContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 10rem auto;
  align-items: center;
  gap: 1.6rem;
  padding: ${theme.spacing.half};
  border-bottom: 1px solid ${theme.colors.blockBorder};

  &:hover {
    background-color: ${theme.colors.N3};
  }
`;

interface Props {
  webhook: Webhook;
}

function WebhookRow(props: Props) {
  const { webhook } = props;
  const queryCache = useQueryCache();

  const { t } = useTranslation();

  const [deleteWebhookMutation, { isLoading: isDeleting }] = useMutation(remoteApi.deleteWebhook, {
    onSuccess: () => {
      queryCache.invalidateQueries(queryKeys.webhooks);
    },
  });

  const [updateWebhookMutation, { isLoading: isUpdating }] = useMutation(remoteApi.updateWebhook, {
    onSuccess: () => {
      queryCache.invalidateQueries(queryKeys.webhooks);
    },
  });

  const menuItems: ReactElement[] = [];

  if (!webhook.enabled) {
    menuItems.push(
      <span key="enable" data-action="enable" data-id={webhook.id}>
        {t('workspaceSettingsWebhooks.enable')}
      </span>,
    );
  } else {
    menuItems.push(
      <span key="disable" data-action="disable" data-id={webhook.id}>
        {t('workspaceSettingsWebhooks.disable')}
      </span>,
    );
  }

  menuItems.push(
    <span key="delete" data-action="delete" data-id={webhook.id}>
      {t('shared.delete')}
    </span>,
  );

  const handleDelete = () => {
    deleteWebhookMutation(webhook.id);
  };

  const handleEnable = () => {
    updateWebhookMutation({
      webhookId: webhook.id,
      webhook: {
        enabled: true,
      },
    });
  };

  const handleDisable = () => {
    updateWebhookMutation({
      webhookId: webhook.id,
      webhook: {
        enabled: false,
      },
    });
  };

  const handleMenuSelection = (value: ReactElement) => {
    const action = value.props['data-action'];

    switch (action) {
      case 'delete':
        if (window.confirm(t('workspaceSettingsWebhooks.deleteConfirm') || '')) {
          handleDelete();
        }
        break;
      case 'enable':
        handleEnable();
        break;
      case 'disable':
        handleDisable();
        break;
    }
  };

  if (isDeleting || isUpdating) {
    return <KoalaLoader />;
  }

  return (
    <WebhookRowContainer>
      <div>{webhook.url}</div>
      <div style={{ display: 'flex' }}>
        <WebhookActiveButton webhook={webhook} />
      </div>

      <div>
        <DropdownMenu
          items={menuItems}
          onSelection={handleMenuSelection}
          trigger={<KoalaIconButton iconName="ellipsis" className="actions" />}
        />
      </div>
    </WebhookRowContainer>
  );
}

export default React.memo(WebhookRow);
